<template>
  <form class="form-invoice-payment" v-on:submit.prevent="shownConfirmDialog = true">
    <the-select
      v-if="isShowCashboxList"
      option-title-key="name"
      option-value-key="id"
      v-bind:optional="false"
      v-bind:options="cashboxList"
      v-bind:value="selectedCashboxId"
      v-on:input="onSelectCashbox"
    />
    <div class="form-invoice-payment__payment-type">
      <p class="form-invoice-payment__payment-type__title">Способ оплаты:</p>
      <div class="form-invoice-payment__switch">
        <v-switch
          class="form-invoice-payment__switch"
          hide-details
          inset
          title="Смешанная оплата"
          v-model="isMixedPayment"
        />
        <span>Смешанная оплата</span>
      </div>
    </div>
    <div class="form-invoice-payment__inputs">
      <div class="form-invoice-payment__mixed" v-if="isMixedPayment">
        <div class="form-invoice-payment__group">
          <label for="cash" class="form-invoice-payment__label">Наличные:</label>
          <price-input
            class="form-invoice-payment__mixed__input"
            name="cash"
            type="text"
            v-bind:with-currency="false"
            v-model="cash"
            inputmode="numeric"
          />
          <span>тнг</span>
        </div>
        <div class="form-invoice-payment__group">
          <label for="electronically" class="form-invoice-payment__label">Карта:</label>
          <price-input
            class="form-invoice-payment__mixed__input"
            name="electronically"
            type="text"
            v-bind:with-currency="false"
            v-model="cashless"
            inputmode="numeric"
          />
          <span>тнг</span>
        </div>
      </div>
      <div class="form-invoice-payment__payment-types" v-else>
        <label class="form-invoice-payment__radio">
          <input
            type="radio"
            name="payment-type"
            v-bind:value="$options.paymentByCash"
            v-model="selectedPaymentType"
          />
          <span>Наличные</span>
        </label>
        <label class="form-invoice-payment__radio">
          <input
            type="radio"
            name="payment-type"
            v-bind:value="$options.paymentByCashless"
            v-model="selectedPaymentType"
          />
          <span>Карта</span>
        </label>
        <template v-if="isOnlineCheckCashbox">
          <div class="form-invoice-payment__qr-code">
            <label class="form-invoice-payment__radio">
              <input
                type="radio"
                name="payment-type"
                v-bind:value="$options.paymentByQRCode"
                v-model="selectedPaymentType"
              />
              <span>QR-код</span>
            </label>
            <p v-if="isSelectedPaymentByQRCode" class="form-invoice-payment__warning">
              Сначала получите перевод денег от клиента, потом проведите оплату в МИС
            </p>
          </div>
        </template>
        <div class="form-invoice-payment__group">
          <span class="form-invoice-payment__label">Сумма к оплате:</span>
          <price-input
            class="form-invoice-payment__mixed__input amount"
            name="amount"
            type="text"
            v-bind:with-currency="false"
            v-model="amountByKop"
            inputmode="numeric"
          />
          <span>тнг</span>
        </div>
      </div>
      <textarea-length-limitation
        v-model="comment"
        title="Комментарий"
        v-bind:max-length="255"
        v-bind:rows="4"
      />
    </div>
    <div class="form-invoice-payment__controls">
      <button
        class="form-invoice-payment__buttons__button-cancel"
        type="button"
        v-on:click="dispatchCancel"
      >
        Отмена
      </button>
      <modal-dialog
        persistent
        title="Подтвердите оплату"
        v-model="shownConfirmDialog"
        with-cross
        max-width="400px"
        width="90%">
        <template v-slot:trigger="{ listenerObject }">
          <button
            class="form-invoice-payment__buttons__button-submit"
            type="button"
            v-on="listenerObject"
            v-bind:disabled="isDisabledPaymentButton"
          >
            Оплатить
          </button>
        </template>
        <div class="form-invoice-payment__confirm-dialog">
          На сумму: <b>
            <the-rub
              v-bind:show-kop="$options.SHOW_KOP_AUTO"
              v-bind:kop="finalAmount"
              currency="тнг" />
          </b>
          <br>
          <div class="form-invoice-payment__confirm-dialog__buttons">
            <button
              class="form-invoice-payment__confirm-dialog__button-cancel"
              type="button"
              v-on:click="shownConfirmDialog = false"
            >
              Отмена
            </button>
            <button
              class="form-invoice-payment__confirm-dialog__button-submit"
              type="button"
              v-bind:disabled="isAwaitRequest"
              v-on:click="onSubmit"
            >
              Подтвердить
            </button>
          </div>
        </div>
      </modal-dialog>
    </div>
  </form>
</template>

<script>
import { PaymentType, AdvanceRechargeRequest } from '@/storage/gateways/InvoiceGateway';
import PriceInput from '@/components/PriceInput.vue';
import TheRub, { SHOW_KOP_AUTO } from '@/components/TheRub.vue';
import ModalDialog from '@/components/ModalDialog.vue';
import TheSelect from '@/components/TheSelect.vue';
import TextareaLengthLimitation from '@/components/TextareaLengthLimitation.vue';

/**
 * @description Название события закрытия формы.
 * @type {string}
 */
const NAME_EVENT_CANCEL = 'cancel';

/**
 * @description Названия события отправки формы.
 * @type {string}
 */
const NAME_EVENT_SUBMIT = 'submit';

/**
 * @description Компонент формы оплаты счета.
 * @type {any}
 */
export default {
  SHOW_KOP_AUTO,
  components: {
    TextareaLengthLimitation,
    TheSelect,
    PriceInput,
    TheRub,
    ModalDialog,
  },
  computed: {
    /**
     * @description Финальная сумма к оплате.
     * @type {number}
     */
    finalAmount() {
      if (this.isMixedPayment) {
        return this.cash + this.cashless;
      }
      return this.amountByKop;
    },
    /**
     * @description Деактивировать кнопку оплаты.
     * @type {boolean}
     */
    isDisabledPaymentButton() {
      if (this.isMixedPayment) {
        return this.cash + this.cashless === 0;
      }
      if (this.selectedPaymentType) {
        return this.amountByKop === 0;
      }
      return true;
    },
    /**
     * @description Показывать список касс.
     * @type {boolean}
     */
    isShowCashboxList() {
      return this.cashboxList.length > 1;
    },
    isOnlineCheckCashbox() {
      if (!this.selectedCashboxId) {
        return false;
      }
      const cashbox = this.cashboxList.find(
        ({ id }) => id.toString() === this.selectedCashboxId.toString(),
      );
      return cashbox ? cashbox.tags.includes('online-check') : false;
    },
    isSelectedPaymentByQRCode() {
      return this.selectedPaymentType === this.$options.paymentByQRCode;
    },
  },
  /**
   * @description Тип оплаты наличными.
   * @type {string}
   */
  paymentByCash: 'cash',
  /**
   * @description Тип оплаты по карте.
   * @type {string}
   */
  paymentByCashless: 'cashless',
  /**
   * @description Тип оплаты по карте.
   * @type {string}
   */
  paymentByQRCode: 'qr-code',
  created() {
    this.cashboxList.slice(0, 1).forEach((cashbox) => {
      this.selectedCashboxId = cashbox.id;
    });
  },
  data() {
    const selectedPaymentType = this.$options.paymentByCash;
    return {
      /**
       * @description Сумма оплаты наличными.
       * @type {number}
       */
      cash: 0,
      /**
       * @description Сумма оплаты по карте.
       * @type {number}
       */
      cashless: 0,
      /**
       * @description Сумма оплаты выбранным способом.
       * @type {number}
       */
      amountByKop: 0,
      /**
       * @description Смешанный тип оплаты.
       * @type {boolean}
       */
      isMixedPayment: false,
      /**
       * @description Выбранный тип оплаты.
       * @type {string}
       */
      selectedPaymentType,
      /**
       * @description Показывать диалог подтверждения оплаты.
       * @type {boolean}
       */
      shownConfirmDialog: false,
      /**
       * @description Идентификатор выборанной кассы.
       */
      selectedCashboxId: null,
      /**
       * @description Комментарий.
       * @type {string}
       */
      comment: '',
    };
  },
  methods: {
    /**
     * @description Отправить событие отмены.
     * @returns {void}
     */
    dispatchCancel() {
      this.$emit(NAME_EVENT_CANCEL);
    },
    /**
     * @description Создать запрос.
     * @returns {PayRequest}
     */
    makeRequest() {
      const paymentTypes = [];
      if (this.isMixedPayment) {
        paymentTypes.push(new PaymentType(this.cash, this.$options.paymentByCash));
        paymentTypes.push(new PaymentType(this.cashless, this.$options.paymentByCashless));
      } else {
        paymentTypes.push(new PaymentType(this.amountByKop, this.selectedPaymentType));
      }
      return new AdvanceRechargeRequest(
        this.patientId,
        paymentTypes,
        this.selectedCashboxId,
        this.comment,
      );
    },
    /**
     * @description Обработать отправку формы.
     * @returns {void}
     */
    onSubmit() {
      const request = this.makeRequest();
      this.$emit(NAME_EVENT_SUBMIT, request);
    },
    onSelectCashbox(cashboxId) {
      this.selectedCashboxId = cashboxId;
      if (this.isSelectedPaymentByQRCode && this.isOnlineCheckCashbox) {
        this.selectedPaymentType = '';
      }
    },
  },
  name: 'FormAdvanceRecharge',
  props: {
    /**
     * @description Запрос выполняется.
     * @type {boolean}
     */
    isAwaitRequest: { type: Boolean, default: false },
    /**
     * @description Идентификатор пациента.
     * @type {number}
     */
    patientId: { type: Number, required: true },
    /**
     * @description Список касс.
     */
    cashboxList: { required: true, type: Array },
  },
};
</script>

<style lang="scss" scoped>
@use "@/scss/buttons";
.form-invoice-payment {
  display: flex;
  flex-direction: column;
  &__buttons {
    &__button-cancel {
      @include buttons.btn-transparent(100%, 56px, 1fr);
    }
    &__button-submit {
      @include buttons.btn-blue(100%, 56px, 1fr);
      &:disabled {
        background-color: var(--unfocus-color);
        border: 1px solid var(--unfocus-color);
        color: white;
      }
    }
  }
  &__confirm-dialog {
    &__buttons {
      display: flex;
      flex-flow: row nowrap;
      margin-top: 18px;
      gap: 8px;
    }
    &__button-cancel {
      @include buttons.btn-transparent(100%, 44px, 16px);
    }
    &__button-submit {
      @include buttons.btn-blue(100%, 44px, 16px);
      &:disabled {
        background-color: var(--unfocus-color);
        border: 1px solid var(--unfocus-color);
        color: white;
      }
    }

  }
  &__head {
    align-items: center;
    column-gap: 15px;
    display: grid;
    grid-template-columns: auto 1fr;
    margin-top: 43px;
    row-gap: 15px;
    padding-bottom: 33px;
    border-bottom: solid 1px #c2cfe0;
    &__amount {
      font-size: 24px;
      font-weight: 600;
      line-height: 29px;
      &.small {
        font-size: 20px;
        font-weight: 600;
        line-height: 24px;
      }
    }
  }
  &__payment {
    &-types {
      display: flex;
      flex-direction: column;
      gap: 20px;
    }
    &-type {
      margin-top: 20px;
      &__title {
        color: var(--reading-color);
        line-height: 24px;
        font-size: 20px;
        font-weight: bold;
      }
    }
  }
  &__switch::v-deep {
    margin-top: 22px;
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 20px;
    span {
      font-size: 18px;
      line-height: 22px;
      color: var(--reading-color);
    }
    .v-input--selection-controls__input:hover .v-input--selection-controls__ripple:before {
      content: none;
    }
    .v-input--selection-controls__ripple {
      display: none;
    }
    .v-input--switch .v-input--switch__track {
      background: white !important;
      border: 1px solid #c2cfe0;
      border-radius: 15px;
      opacity: 1 !important;
    }
    .v-input--switch .v-input--switch__thumb {
      color: #90a0b7;
    }
    .v-input--selection-controls {
      margin: 0 !important;
      padding: 5px !important;
    }
  }
  &__radio {
    & > input {
      position: absolute;
      z-index: -1;
      opacity: 0;
    }
    & > span {
      display: inline-flex;
      align-items: center;
      user-select: none;
      font-weight: 400;
      font-size: 16px;
      line-height: 19px;
      cursor: pointer;
    }
    & > span::before {
      content: "";
      display: inline-block;
      width: 24px;
      height: 24px;
      flex-shrink: 0;
      flex-grow: 0;
      border: 1px solid #90a0b7;
      border-radius: 50%;
      margin-right: 10px;
      background-repeat: no-repeat;
      background-position: center center;
      background-size: 50% 50%;
    }
    & > input:not(:disabled):not(:checked) + span:hover::before {
      border: 1px solid #0489e0;
    }
    & > input:checked + span::before {
      border: 1px solid #0489e0;
      background-color: #0489e0;
      box-shadow: inset 0 0 0 6px #fff;
    }
  }
  &__controls {
    margin-top: 61px;
    column-gap: 9px;
    display: grid;
    grid-template-columns: repeat(2, 214px);
    justify-content: flex-end;
  }
  &__inputs {
    margin: 22px 0;
  }
  &__mixed {
    display: grid;
    grid-template-columns: 1fr;
    row-gap: 15px;
    &__input {
      border-radius: 4px;
      border: 1px solid #c2cfe0;
      outline: 0;
      padding: 5px 10px;
      height: 36px;
      &.amount {
        padding: 5px 10px;
      }
      &:focus {
        border-color: var(--action-color);
      }
    }
  }
  &__group {
    display: grid;
    grid-template-columns: 156px 1fr 30px;
    align-items: center;
    column-gap: 8px;
  }
  &__qr-code {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  &__warning {
    color: red;
    font-size: 14px;
  }
}
@media screen and (max-width: 520px) {
  .form-invoice-payment__controls {
    grid-template-columns: 1fr 1fr;
    justify-content: auto;
  }
}
@media screen and (max-width: 480px) {
  .form-invoice-payment {
    &__controls {
      grid-template-columns: 1fr;
      row-gap: 10px;
      margin-top: 20px;
    }
    &__group {
      display: grid;
      grid-template-columns: 1fr 30px;
      align-items: center;
      column-gap: 8px;
      position: relative;
    }
    &__label {
      color: var(--unfocus-color);
      font-weight: 400;
      font-size: 12px;
      line-height: 16px;
      margin: 0 0 2px 0;
      grid-column: 1/3;
    }
    &__confirm-dialog {
      &__buttons {
        flex-wrap: wrap;
      }
    }
  }
}
</style>
